import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const Textarea = forwardRef(
  (
    {
      label,
      error,
      required = false,
      className = "",
      onFocus: propOnFocus,
      onBlur: propOnBlur,
      onChange: propOnChange,
      defaultValue,
      rows = 4,
      autoResize = false,
      resize = "vertical", // "none", "vertical", "horizontal", "both"
      maxRows,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslationDetector();
    const [isFocused, setIsFocused] = useState(false);

    // hasInputValue'nun başlangıç değerini defaultValue'ya göre ayarla
    const [hasInputValue, setHasInputValue] = useState(() => {
      return defaultValue !== undefined && String(defaultValue).length > 0;
    });

    // Internal ref for our own use
    const internalRef = useRef(null);

    // Forward the ref to both React Hook Form and our internal use
    useImperativeHandle(ref, () => internalRef.current);

    const hasPlaceholder = props.placeholder && props.placeholder.length > 0;

    const shouldFloat = isFocused || hasInputValue || hasPlaceholder;

    // Auto resize functionality
    const adjustHeight = useCallback(() => {
      if (!autoResize || !internalRef.current) return;

      const textarea = internalRef.current;
      textarea.style.height = "auto";

      let newHeight = textarea.scrollHeight;

      if (maxRows) {
        const lineHeight = parseInt(
          window.getComputedStyle(textarea).lineHeight,
        );
        const maxHeight = lineHeight * maxRows;
        newHeight = Math.min(newHeight, maxHeight);
      }

      textarea.style.height = `${newHeight}px`;
    }, [autoResize, maxRows]);

    // Adjust height on mount and when defaultValue changes
    useEffect(() => {
      if (autoResize) {
        adjustHeight();
      }
    }, [adjustHeight]);

    const handleFocus = useCallback(
      (e) => {
        setIsFocused(true);
        propOnFocus?.(e);
      },
      [propOnFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocused(false);

        const textareaValue = internalRef?.current?.value || "";
        const hasValue = textareaValue.length > 0;
        setHasInputValue(hasValue);

        propOnBlur?.(e);
      },
      [propOnBlur],
    );

    const handleChange = useCallback(
      (e) => {
        if (autoResize) {
          adjustHeight();
        }

        const textareaValue = e.target.value;
        const hasValue = textareaValue.length > 0;
        setHasInputValue(hasValue);

        propOnChange?.(e);
      },
      [propOnChange, autoResize, adjustHeight],
    );

    // Memoized class names to prevent unnecessary clsx recalculations
    const textareaClassName = useCallback(
      () =>
        clsx(
          "peer w-full rounded-md border p-4 font-medium focus:ring-2 focus:outline-none",
          // Focus colors - error durumunda danger, normal durumda primary
          error
            ? "focus:border-danger focus:ring-danger border-danger"
            : "focus:border-primary focus:ring-primary border-border",
          props.disabled && "bg-background-global cursor-not-allowed",
          label && "pt-4",
          // Resize davranışı
          resize === "none" && "resize-none",
          resize === "vertical" && "resize-y",
          resize === "horizontal" && "resize-x",
          resize === "both" && "resize",
          // Auto resize için overflow gizle
          autoResize && "resize-none overflow-hidden",
        ),
      [error, props.disabled, label, resize, autoResize],
    );

    const labelClassName = useCallback(
      () =>
        clsx(
          "pointer-events-none absolute left-4 flex items-center gap-1 transition-all duration-200",
          shouldFloat ? "-top-2 bg-white px-2 text-xs" : "top-4 text-base",
          error
            ? "text-danger"
            : isFocused
              ? "text-primary"
              : "text-foreground-muted",
          props.disabled && "text-gray-400",
        ),
      [shouldFloat, error, props.disabled, isFocused],
    );

    return (
      <div className={clsx("flex flex-col gap-2", className)}>
        <div className="relative">
          <textarea
            ref={internalRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            defaultValue={defaultValue}
            rows={autoResize ? 1 : rows}
            className={textareaClassName()}
            {...props}
          />

          {label && (
            <label htmlFor={props.id} className={labelClassName()}>
              <span>{label}</span>
            </label>
          )}
        </div>

        {error && (
          <p className="text-danger flex items-center gap-1 pl-4 text-xs">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>
              {typeof error === "string" ? error : t("common.required")}
            </span>
          </p>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

// Memo ile props karşılaştırması
const MemoizedTextarea = memo(Textarea);
MemoizedTextarea.displayName = "Textarea";

export default MemoizedTextarea;
